import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { subscribeToPush } from '@/utils/pushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'livon_notif_prompt_dismissed';
const MIN_ACCOUNT_AGE_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

export default function NotificationPrompt() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !token) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (!('Notification' in window) || !('PushManager' in window) || !('serviceWorker' in navigator)) return;
    if (Notification.permission !== 'default') return;

    // Don't compete with the install prompt (same corner) — wait until it's been dismissed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (!isStandalone && !localStorage.getItem('install-prompt-dismissed')) return;

    // Only ask users who have come back (account at least 2 days old)
    try {
      const created = new Date(user.created_at);
      if (Date.now() - created.getTime() < MIN_ACCOUNT_AGE_MS) return;
    } catch (e) {
      return;
    }

    const timer = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(timer);
  }, [user, token]);

  const handleAccept = async () => {
    setLoading(true);
    try {
      const result = await subscribeToPush(token);
      if (!result.success) {
        if (result.reason === 'denied') {
          toast.error('Du behöver tillåta notiser i webbläsarens inställningar');
        } else {
          toast.error('Kunde inte aktivera notiser');
        }
        return;
      }
      // Enable the default daily reminder at 19:00
      await axios.put(`${API}/reminders/settings`, {
        reminders: { daily: { enabled: true, time: '19:00' } }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Daglig påminnelse aktiverad kl 19:00. Ändra tid under Påminnelser.');
    } catch (e) {
      toast.error('Något gick fel. Prova igen under Påminnelser.');
    } finally {
      localStorage.setItem(DISMISS_KEY, '1');
      setVisible(false);
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-[60] bg-white rounded-2xl border border-border/40 shadow-xl p-5 animate-slide-up"
      data-testid="notification-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1"
        data-testid="notification-prompt-close"
        aria-label="Stäng"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-primary" />
        </div>
        <div className="space-y-3 pr-4">
          <div>
            <p className="font-medium">Vill du få en daglig påminnelse?</p>
            <p className="text-sm text-muted-foreground mt-1">
              En vänlig notis kl 19:00 som hjälper dig hålla dina rutiner — även när appen är stängd.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleAccept}
              disabled={loading}
              size="sm"
              className="rounded-full"
              data-testid="notification-prompt-accept"
            >
              Ja, aktivera
            </Button>
            <Button
              onClick={() => { handleDismiss(); navigate('/reminders'); }}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="notification-prompt-settings"
            >
              Välj själv
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              className="rounded-full text-muted-foreground"
              data-testid="notification-prompt-dismiss"
            >
              Nej tack
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
